/**
 * Loads the Google reCAPTCHA Enterprise script exactly once.
 *
 * - Injects `<script src="…/enterprise.js?render=<siteKey>">` into `<head>`.
 * - Returns a `Promise<void>` that resolves on `onload` and rejects on `onerror`.
 * - Idempotent: subsequent calls with the same key resolve immediately.
 */

const SCRIPT_ID = "recaptcha-enterprise-script";

let loadPromise: Promise<void> | null = null;

export function loadRecaptchaScript(siteKey: string): Promise<void> {
  // Already loaded or loading — return the same promise
  if (loadPromise) return loadPromise;

  // Script may already be in the DOM (e.g. added manually in index.html)
  if (document.getElementById(SCRIPT_ID)) {
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => {
      // Allow retry on next call
      loadPromise = null;
      reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}
